/**
 * Custom hook for navigation with authentication and route protection
 */

import { useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../store/auth-store'
import {
  canAccessRoute,
  getDefaultRedirectForUser,
  getRouteRequirements,
  isSafeRedirectUrl,
  type NavigationState,
} from '../lib/navigation-guards'

interface UseNavigationReturn {
  navigateWithGuards: (to: string, options?: { replace?: boolean; state?: unknown }) => void
  navigateToDefault: () => void
  navigateToLogin: () => void
  handleLoginRedirect: (returnUrl?: string) => void
  getDefaultRedirect: () => string
  getReturnUrl: () => string | null
  canAccessCurrentRoute: () => { allowed: boolean; redirectTo?: string; reason?: string }
  navigationState: NavigationState
}

export function useNavigation(): UseNavigationReturn {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user, isProfileComplete } = useAuthStore()

  const navigationState: NavigationState = useMemo(
    () => ({
      isAuthenticated: isAuthenticated(),
      user,
      isProfileComplete,
    }),
    [isAuthenticated, user, isProfileComplete]
  )

  /**
   * Navigate to a route with automatic redirect handling
   */
  const navigateWithGuards = useCallback(
    (to: string, options?: { replace?: boolean; state?: unknown }) => {
      const requirements = getRouteRequirements(to)
      const result = canAccessRoute(navigationState, requirements)

      if (result.allowed) {
        navigate(to, options)
      } else if (result.redirectTo) {
        navigate(result.redirectTo, { replace: true, ...options })
      }
    },
    [navigate, navigationState]
  )

  /**
   * Get the appropriate redirect URL for the current user state
   */
  const getDefaultRedirect = useCallback(() => {
    return getDefaultRedirectForUser(navigationState)
  }, [navigationState])

  /**
   * Navigate to the default route for the current user
   */
  const navigateToDefault = useCallback(() => {
    const defaultRoute = getDefaultRedirect()
    navigate(defaultRoute, { replace: true })
  }, [navigate, getDefaultRedirect])

  /**
   * Handle login redirect with return URL support
   */
  const handleLoginRedirect = useCallback(
    (returnUrl?: string) => {
      if (returnUrl && isSafeRedirectUrl(returnUrl)) {
        // Check if user can access the return URL
        const requirements = getRouteRequirements(returnUrl)
        const result = canAccessRoute(navigationState, requirements)

        if (result.allowed) {
          navigate(returnUrl, { replace: true })
          return
        } else if (result.redirectTo) {
          navigate(result.redirectTo, { replace: true })
          return
        }
      }

      // Fall back to default redirect
      navigateToDefault()
    },
    [navigate, navigateToDefault, navigationState]
  )

  /**
   * Get return URL from current location
   */
  const getReturnUrl = useCallback(() => {
    const urlParams = new URLSearchParams(location.search)
    const returnUrl = urlParams.get('returnUrl')

    if (returnUrl && isSafeRedirectUrl(decodeURIComponent(returnUrl))) {
      return decodeURIComponent(returnUrl)
    }

    return null
  }, [location.search])

  /**
   * Navigate to login with current location as return URL
   */
  const navigateToLogin = useCallback(() => {
    const currentPath = location.pathname + location.search
    const returnUrl = encodeURIComponent(currentPath)
    navigate(`/auth/login?returnUrl=${returnUrl}`)
  }, [navigate, location])

  /**
   * Check if current route is accessible
   */
  const canAccessCurrentRoute = useCallback(() => {
    const requirements = getRouteRequirements(location.pathname)
    return canAccessRoute(navigationState, requirements)
  }, [location.pathname, navigationState])

  return {
    navigateWithGuards,
    navigateToDefault,
    navigateToLogin,
    handleLoginRedirect,
    getDefaultRedirect,
    getReturnUrl,
    canAccessCurrentRoute,
    navigationState,
  }
}

/**
 * Hook for checking route access without navigation
 */
export function useRouteAccess(path?: string) {
  const location = useLocation()
  const { isAuthenticated, user, isProfileComplete } = useAuthStore()

  const navigationState: NavigationState = useMemo(
    () => ({
      isAuthenticated: isAuthenticated(),
      user,
      isProfileComplete,
    }),
    [isAuthenticated, user, isProfileComplete]
  )

  const targetPath = path || location.pathname
  const requirements = getRouteRequirements(targetPath)
  const result = canAccessRoute(navigationState, requirements)

  return {
    canAccess: result.allowed,
    redirectTo: result.redirectTo,
    reason: result.reason,
    requirements,
  }
}
